/**
 * Hook para detectar e gerenciar comentários dentro de uma conversa
 * Usado para exibir preview de postagens e toggle de resposta
 * Gera entradas de postagem para exibição inline no chat
 * 
 * @version 2.0.0
 * @date 2026-01-18
 */
import { useMemo, useCallback } from 'react';
import { Message } from '@/pages/tenant/Chat';

// Interface do comentário detectado
export interface ConversationComment {
  /** ID da mensagem */
  messageId: string;
  /** ID do comentário no Instagram */
  commentId: string;
  /** ID da postagem/mídia */
  mediaId: string;
  /** Tipo da mídia (FEED, REELS, etc) */
  mediaType: string;
  /** URL da mídia (se disponível) */
  mediaUrl?: string;
  /** Link direto para postagem no Instagram (permalink) */
  mediaPermalink?: string;
  /** Username do autor do comentário */
  username?: string;
  /** Texto do comentário */
  text: string;
  /** Data/hora do comentário */
  sentAt: string;
  /** Se resposta privada já foi enviada */
  privateReplySent?: boolean;
  /** Data da resposta privada */
  privateReplySentAt?: string;
}

// Interface para entrada de postagem (card inline)
export interface PostEntry {
  /** ID único da postagem (media_id) */
  postId: string;
  /** URL da mídia */
  mediaUrl?: string;
  /** Tipo de mídia */
  mediaType: string;
  /** Permalink do Instagram */
  mediaPermalink?: string;
  /** Timestamp do primeiro comentário */
  firstCommentAt: string;
  /** Quantidade de comentários nesta postagem */
  commentCount: number;
  /** IDs das mensagens de comentário vinculadas */
  commentMessageIds: string[];
}

// Interface para metadados de comentário
interface CommentMetadata {
  source?: string;
  comment_id?: string;
  media_id?: string;
  media_type?: string;
  media_url?: string;
  media_permalink?: string;
  username?: string;
  is_public?: boolean;
  private_reply_sent?: boolean;
  private_reply_sent_at?: string;
  [key: string]: unknown;
}

// Verifica se a mensagem é um comentário do Instagram
export function isInstagramComment(message: Message): boolean {
  return (
    message.message_type === 'comment' ||
    (message.metadata as CommentMetadata)?.source === 'instagram-comment'
  );
}

// Verifica se pode enviar resposta privada (7 dias e não enviada ainda)
export function canSendPrivateReply(comment: ConversationComment): boolean {
  // Verifica se já foi enviada resposta privada
  if (comment.privateReplySent) return false;
  
  // Verifica janela de 7 dias
  const commentDate = new Date(comment.sentAt);
  const now = new Date();
  const daysDiff = (now.getTime() - commentDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= 7;
}

/**
 * Hook para detectar e gerenciar comentários em uma conversa
 * Gera entradas de postagem para exibição inline no chat
 * @param messages Lista de mensagens da conversa
 * @returns Objeto com informações sobre comentários e postagens
 */
export function useConversationComments(messages: Message[]) {
  // Detecta todos os comentários inbound (do contato)
  const comments = useMemo<ConversationComment[]>(() => {
    return messages
      .filter(msg => msg.direction === 'inbound' && isInstagramComment(msg))
      .map(msg => {
        const meta = msg.metadata as CommentMetadata;
        return {
          messageId: msg.id,
          commentId: meta?.comment_id || msg.external_id || '',
          mediaId: meta?.media_id || '',
          mediaType: meta?.media_type || 'FEED',
          mediaUrl: meta?.media_url,
          mediaPermalink: meta?.media_permalink,
          username: meta?.username,
          text: msg.content || '',
          sentAt: msg.sent_at,
          privateReplySent: meta?.private_reply_sent,
          privateReplySentAt: meta?.private_reply_sent_at,
        };
      })
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [messages]);

  // Último comentário recebido (mais recente)
  const lastComment = useMemo(() => {
    return comments.length > 0 ? comments[0] : null;
  }, [comments]);

  // Verifica se a conversa contém algum comentário
  const hasComments = comments.length > 0;

  // Verifica se pode enviar resposta privada para o último comentário
  const canSendPrivate = useMemo(() => {
    return lastComment ? canSendPrivateReply(lastComment) : false;
  }, [lastComment]);

  // Agrupa comentários por postagem (media_id)
  const commentsByPost = useMemo(() => {
    const grouped: Record<string, ConversationComment[]> = {};
    comments.forEach(comment => {
      if (!grouped[comment.mediaId]) {
        grouped[comment.mediaId] = [];
      }
      grouped[comment.mediaId].push(comment);
    });
    return grouped;
  }, [comments]);

  // Conta postagens únicas comentadas
  const uniquePostsCount = Object.keys(commentsByPost).length;

  /**
   * Gera entradas de postagem (PostEntry) para exibição inline no chat
   * Cada postagem única gera um card que aparece na posição do primeiro comentário
   */
  const postEntries = useMemo<PostEntry[]>(() => {
    const entries: PostEntry[] = [];
    
    Object.entries(commentsByPost).forEach(([mediaId, postComments]) => {
      if (!mediaId || postComments.length === 0) return;
      
      // Ordena comentários por data (mais antigo primeiro) para posição no chat
      const sortedByOldest = [...postComments].sort(
        (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
      );
      
      // Primeiro comentário define posição no chat
      const firstComment = sortedByOldest[0];
      
      // CORREÇÃO: Busca dados de mídia do comentário mais recente que tiver
      // Comentários antigos podem não ter media_url (processados antes do fix no webhook)
      // Prioriza comentários mais recentes que geralmente têm dados completos
      const commentWithMedia = [...postComments]
        .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
        .find(c => c.mediaUrl || c.mediaPermalink);
      
      entries.push({
        postId: mediaId,
        // Usa mediaUrl do comentário que tiver dados, fallback para primeiro
        mediaUrl: commentWithMedia?.mediaUrl || firstComment.mediaUrl,
        mediaType: commentWithMedia?.mediaType || firstComment.mediaType,
        mediaPermalink: commentWithMedia?.mediaPermalink || firstComment.mediaPermalink,
        firstCommentAt: firstComment.sentAt,
        commentCount: postComments.length,
        commentMessageIds: postComments.map(c => c.messageId),
      });
    });
    
    // Ordena por data do primeiro comentário (mais antigo primeiro)
    return entries.sort(
      (a, b) => new Date(a.firstCommentAt).getTime() - new Date(b.firstCommentAt).getTime()
    );
  }, [commentsByPost]);

  /**
   * Mapeia ID de mensagem para dados da postagem correspondente
   * Usado para obter dados da postagem ao renderizar um comentário
   */
  const commentToPostMap = useMemo(() => {
    const map = new Map<string, PostEntry>();
    
    postEntries.forEach(entry => {
      entry.commentMessageIds.forEach(msgId => {
        map.set(msgId, entry);
      });
    });
    
    return map;
  }, [postEntries]);

  /**
   * Retorna os dados da postagem para um comentário específico
   * @param messageId ID da mensagem de comentário
   * @returns PostEntry ou null se não for um comentário ou postagem não encontrada
   */
  const getPostForComment = useCallback((messageId: string): PostEntry | null => {
    return commentToPostMap.get(messageId) || null;
  }, [commentToPostMap]);

  /**
   * Verifica se uma mensagem específica é um comentário
   * @param messageId ID da mensagem
   * @returns true se for um comentário, false caso contrário
   */
  const isCommentMessage = useCallback((messageId: string): boolean => {
    return commentToPostMap.has(messageId);
  }, [commentToPostMap]);

  return {
    /** Lista de todos os comentários (ordenados por data desc) */
    comments,
    /** Último comentário recebido */
    lastComment,
    /** Se a conversa contém comentários */
    hasComments,
    /** Se pode enviar resposta privada para o último comentário */
    canSendPrivateReply: canSendPrivate,
    /** Comentários agrupados por postagem */
    commentsByPost,
    /** Quantidade de postagens únicas comentadas */
    uniquePostsCount,
    /** Entradas de postagem para exibição inline no chat */
    postEntries,
    /** Função para obter dados da postagem de um comentário */
    getPostForComment,
    /** Verifica se uma mensagem é um comentário */
    isCommentMessage,
  };
}
